/**
 * Schemi di cablaggio: dal nome del documento all'immagine pubblicata.
 *
 * La knowledge base `kg_SCHEMI` contiene le *descrizioni testuali* degli schemi,
 * con nomi nella forma `SCHEMA__<Modello>__p<NNN>__<tipo>`. Le immagini vere sono
 * pubblicate su schemi.datacomtecnologie.it come `<Modello>_p<NNN>.png`.
 *
 * Le due cose esistevano da giugno ma non erano mai state collegate: il bot citava
 * lo schema senza mostrarlo. È la segnalazione del 22/07 — «richiama la scheda
 * tecnica giusta, ma non dà la foto del cablaggio che è necessaria».
 */

const SCHEMI_BASE = "https://schemi.datacomtecnologie.it";

/**
 * Da dove accettiamo un'immagine dentro una risposta.
 *
 * Il testo delle risposte lo scrive il modello, e il markdown puo' contenere
 * qualunque indirizzo: un documento avvelenato in archivio, o una semplice
 * invenzione, basterebbero a far caricare al telefono dell'installatore una
 * risorsa di un estraneo. Gli schemi legittimi hanno una casa sola.
 */
const DOMINI_AMMESSI = ["schemi.datacomtecnologie.it", "ai.datacomtecnologie.it"];

/** true se l'immagine puo' essere mostrata: casa nostra, o un dato incorporato. */
export function immagineAmmessa(src: string): boolean {
  const s = (src ?? "").trim();
  if (!s) return false;
  // Le immagini incorporate nella pagina (data:) e i percorsi relativi sono
  // nostri per definizione.
  if (s.startsWith("data:image/")) return true;
  // «//dominio/x.png» NON e' un percorso relativo: e' un indirizzo senza
  // schema, che il browser completa col protocollo della pagina e porta
  // fuori casa (06/09/2026, secondo giro di Astra 6, A2).
  if (s.startsWith("//")) return false;
  if (s.startsWith("/")) return true;
  try {
    const u = new URL(s, window.location.href);
    if (u.protocol !== "https:" && u.protocol !== "http:") return false;
    return DOMINI_AMMESSI.includes(u.hostname) || u.hostname === window.location.hostname;
  } catch {
    return false;
  }
}

/** `SCHEMA__Previdia_Compact__p018__bus` → modello, pagina, tipo di schema. */
const NOME_SCHEMA = /^SCHEMA__(.+?)__p(\d+)__(.+?)(?:\.pdf)?$/i;

export interface SchemaRiferimento {
  /** Es. "Previdia Compact" — leggibile, per l'etichetta. */
  modello: string;
  /** Numero di pagina nel manuale originale. */
  pagina: number;
  /** Es. "bus", "sirene", "loop" — cosa mostra lo schema. */
  tipo: string;
  /** URL pubblico dell'immagine. */
  url: string;
}

/**
 * Riconosce un documento della KB schemi e ne ricava l'immagine.
 * Restituisce null se il nome non segue la convenzione: in quel caso la citazione
 * resta un normale riferimento testuale.
 */
export function riconosciSchema(documentName?: string | null): SchemaRiferimento | null {
  const nome = (documentName ?? "").trim();
  const m = nome.match(NOME_SCHEMA);
  if (!m) return null;

  const [, modelloRaw, paginaRaw, tipoRaw] = m;
  const pagina = Number(paginaRaw);
  if (!Number.isFinite(pagina)) return null;

  return {
    modello: modelloRaw.replace(/_/g, " ").trim(),
    pagina,
    tipo: tipoRaw.replace(/_/g, " ").trim(),
    // Il file conserva gli underscore e lo zero-padding del nome originale.
    url: `${SCHEMI_BASE}/${modelloRaw}_p${paginaRaw}.png`,
  };
}

/**
 * Lo schema parla DAVVERO del prodotto di cui parla la risposta?
 *
 * Il recupero e' semantico: a una domanda sull'AM4000 puo' allegare la
 * morsettiera dell'AM-8200N, e a una sul pannello AM-LCD quella dell'AM-8200G
 * (misurato sulle 55 domande della riunione, 04/09/2026). Su un testo sbagliato
 * l'installatore inciampa e se ne accorge; a un DISEGNO sbagliato crede, e
 * cabla la centrale seguendo la morsettiera di un'altra. Per questo l'immagine
 * si mostra solo se il modello dello schema compare nella risposta o nella
 * domanda: meglio nessuno schema che quello di un'altra centrale.
 */
function compatta(s: string): string {
  return (s ?? "").toUpperCase().replace(/[^A-Z0-9]+/g, "");
}

/** Le parole di un testo, intere: "MultiTransmitter" e' UN gettone, non due. */
function gettoni(s: string): Set<string> {
  return new Set((s ?? "").toUpperCase().match(/[A-Z0-9]+/g) ?? []);
}

/**
 * Le parole di un testo E le loro sequenze attaccate, fino a quattro di fila.
 *
 * 05/09/2026 (famiglie AM di Notifier): il manuale scrive «AM-2000-N», il
 * costruttore «AM2000N», l'installatore «am 2000 n». Sono tre grafie dello
 * stesso nome, ma per il confronto a parole intere erano tre cose diverse:
 * «AM-2000-N» si spezza in AM, 2000, N e il gettone «AM2000N» non c'e'. Qui
 * si aggiungono le sequenze di gettoni ADIACENTI incollati (AM+2000 = AM2000,
 * AM+2000+N = AM2000N): resta un confronto fra parole intere - «TRANSMITTER»
 * non sta dentro «MULTITRANSMITTER», perche' non e' una sequenza di gettoni -
 * ma il trattino e lo spazio non separano piu' un nome da se stesso.
 */
function gettoniESequenze(s: string): Set<string> {
  const parole = (s ?? "").toUpperCase().match(/[A-Z0-9]+/g) ?? [];
  const insieme = new Set(parole);
  for (let i = 0; i < parole.length; i++) {
    let seq = parole[i];
    for (let j = i + 1; j < Math.min(parole.length, i + 4); j++) {
      seq += parole[j];
      insieme.add(seq);
    }
  }
  return insieme;
}

export function schemaCoerente(
  schema: SchemaRiferimento,
  ...testi: (string | null | undefined)[]
): boolean {
  const modello = compatta(schema.modello);
  if (!modello) return false;
  // Si confrontano PAROLE INTERE, non sottostringhe. Fino al 05/09/2026 il
  // confronto era sul testo compattato: "TRANSMITTER" stava dentro
  // "MULTITRANSMITTER", e lo schema del Transmitter Fibra poteva comparire
  // sotto una risposta sul MultiTransmitter — un disegno di un altro
  // dispositivo, che e' esattamente l'errore che questa funzione esiste per
  // impedire. Con 212 modelli in archivio le sigle una dentro l'altra sono
  // la regola, non l'eccezione (Hub / Hub 2, ReX / ReX 2, KeyPad / KeyPad Plus).
  const dove = gettoniESequenze(testi.filter(Boolean).join(" "));
  // scritto tutto attaccato ("AM8200N"), o a pezzi adiacenti ("AM-8200N",
  // "AM 8200 N"): il nome intero, comunque sia stato spezzato
  if (dove.has(modello)) return true;
  // "Superior Transmitter Fibra" nel nome del file, "Transmitter Fibra" nel
  // testo: le parole del modello contano una per una, e devono esserci tutte.
  // Una parola col trattino ("AM-8200N", "221-21X") vale se c'e' intera oppure
  // se ci sono tutti i suoi pezzi da tre caratteri in su ("8200N", "221" e "21X").
  const parole = (schema.modello.match(/[A-Za-z0-9-]+/g) ?? []).filter(
    (p) => p.length > 2 && !/^(di|da|per|the|superior)$/i.test(p),
  );
  return (
    parole.length > 0 &&
    parole.every((p) => {
      if (dove.has(compatta(p))) return true;
      const pezzi = (p.match(/[A-Za-z0-9]+/g) ?? []).filter((x) => x.length > 2);
      return pezzi.length > 0 && pezzi.every((x) => dove.has(x.toUpperCase()));
    })
  );
}

/**
 * Le parole con cui si chiede ognuno dei tipi di schema che abbiamo in archivio.
 * Servono a scartare la pagina sbagliata DELLA CENTRALE GIUSTA: alla domanda
 * sul pannello remoto in rete Hornet+ il recupero allegava la pagina del LOOP
 * della Previdia Compact invece di quella del BUS (04/09/2026).
 */
const PAROLE_DEL_TIPO: Record<string, RegExp> = {
  bus: /\b(bus|i-?bus|hornet|rs-?485|seriale|pannell[oi] remot|repeater|tastier|espansion)/i,
  loop: /\b(loop|linea indirizzat|linee indirizzat|rivelator|rilevator|analogic)/i,
  sirene: /\b(siren|blind|autoalimentat)/i,
  ingressi: /\b(ingress|zona|zone|contatt|bilanciat|sensor)/i,
  rele: /\b(rel[eè]|uscit|contatto pulito|nc\b|na\b)/i,
  telefonico: /\b(telefon|combinator|pstn|linea telefonica|gsm)/i,
  alimentazione: /\b(aliment|batteri|24 ?v|assorbiment|trasformator)/i,
};

/**
 * true se la richiesta parla chiaramente di un ALTRO tipo di schema.
 * Nel dubbio (nessun tipo riconosciuto, o piu' d'uno) non si scarta niente:
 * questo filtro serve a evitare l'errore evidente, non a fare il difficile.
 */
function tipoInConflitto(schema: SchemaRiferimento, testo: string): boolean {
  const mio = Object.keys(PAROLE_DEL_TIPO).find((k) =>
    schema.tipo.toLowerCase().includes(k),
  );
  if (!mio) return false; // "generale", "morsettiera": valgono per tutto
  if (PAROLE_DEL_TIPO[mio].test(testo)) return false; // parla proprio del mio
  const altri = Object.entries(PAROLE_DEL_TIPO).filter(
    ([k, re]) => k !== mio && re.test(testo),
  );
  return altri.length === 1; // un altro tipo, uno solo, senza ambiguita'
}

/**
 * Lo schema si puo' MOSTRARE dentro la risposta: modello giusto e argomento
 * non in conflitto. Chi non passa resta comunque citato fra le fonti, dove
 * un riferimento sbagliato si legge e si scarta, mentre un disegno sbagliato
 * si segue.
 */
/**
 * Lo schema e' di un FRATELLO del prodotto chiesto?
 *
 * 05/09/2026: alla domanda «schema di cablaggio dell'Hub 2 Jeweller» la
 * risposta spiegava che l'Hub 2 e' radio e che per il cablato serve l'Hub
 * Hybrid — e nominandolo faceva passare il disegno della morsettiera del
 * Superior Hub Hybrid 2 sotto una domanda su un hub senza morsetti. Con 212
 * modelli in archivio, i nomi che condividono una parola («Hub», «KeyPad»,
 * «221-») e ne aggiungono una («Hybrid», «TouchScreen», «21I») sono la regola.
 *
 * Regola: se la domanda condivide almeno una parola distintiva col modello
 * dello schema ma NON ne contiene un'altra, altrettanto distintiva, allora la
 * domanda parla del fratello, e il disegno non e' suo. Se la domanda non
 * condivide niente (es. «pannello remoto su Hornet+»), non si giudica qui: e'
 * la risposta a dire di che prodotto si parla, come prima.
 */
/**
 * Le parole che NON distinguono un prodotto dal fratello. Solo «superior» (la
 * linea cablata Ajax: «Superior KeyPad Fibra» e «KeyPad Fibra» sono lo stesso
 * oggetto) e le preposizioni. Fino al 05/09/2026 (audit, Fable 3) qui c'erano
 * anche plus, outdoor, jeweller e fibra: ma su Ajax sono ESATTAMENTE le parole
 * che separano i fratelli, e per tutt'e due c'e' un disegno in archivio -
 * MotionProtect Fibra / MotionProtect PLUS Fibra, KeyPad Fibra / KeyPad
 * OUTDOOR Fibra, StreetSiren Fibra / StreetSiren PLUS Fibra, DoorProtect Fibra
 * / DoorProtect PLUS Fibra, MultiTransmitter Fibra / G3 JEWELLER, WallSwitch /
 * WallSwitch JEWELLER. Con quelle parole in lista il disegno della Plus
 * passava sotto la domanda sulla non-Plus: cinque casi su sei provati.
 */
const PAROLE_DI_FAMIGLIA = /^(superior|the|per|di|da)$/i;

/**
 * Una parola «distintiva» del modello: piu' lunga di due lettere, oppure una
 * sigla con cifre anche corta («21X», «21I», «G3»): 221-21X e 221-21I si
 * distinguono per un carattere, e va contato.
 */
function paroleDistintive(modello: string): string[] {
  return (modello.match(/[A-Za-z0-9]+/g) ?? [])
    .filter((p) => (p.length > 2 || /\d/.test(p)) && !PAROLE_DI_FAMIGLIA.test(p))
    .map((p) => p.toUpperCase());
}

/**
 * La radice di una sigla: le cifre con quel che le precede, senza la coda di
 * una o due lettere. «8200N» e «8200G» hanno la stessa radice («8200»), come
 * «21X» e «21I» («21») e «AM2000» / «AM2000N» («AM2000»). Vuota se la sigla
 * non ha cifre o la radice e' troppo corta per dire qualcosa («2G» -> «2»:
 * un numero solo sta in troppe frasi per fare da nome).
 */
function radiceSigla(gettone: string): string {
  if (!/\d/.test(gettone)) return "";
  const r = gettone.replace(/[A-Z]{1,2}$/, "");
  return r.length >= 2 && /\d/.test(r) ? r : "";
}

/**
 * Due sigle GEMELLE: stessa radice, diverse solo nella coda di una o due
 * lettere (o una delle due ce l'ha e l'altra no).
 *
 * 06/09/2026 (secondo giro di Astra 6, A1): «schema AM-8200G» con una
 * risposta che nomina anche la AM-8200N faceva passare il disegno della N,
 * perche' «8200G» e «8200N» sono parole intere diverse e la regola qui sotto
 * vede una parola sola nel nome («AM» e' corta), quindi non giudica. Ma le
 * due centrali sono diverse quanto due centrali possono esserlo (la G e' la
 * versione gas), e lo stesso vale per 221-21X / 221-21I e AM2000 / AM2000N.
 */
function sigleGemelle(a: string, b: string): boolean {
  if (a === b) return false;
  const ra = radiceSigla(a);
  return ra !== "" && ra === radiceSigla(b);
}

/**
 * Un gettone che nomina un PRODOTTO: cifre dentro, almeno tre caratteri,
 * non un'unita' di misura («24V», «14MA», «7AH», «2000M») e, se e' solo un
 * numero, almeno quattro cifre («8100», «1050»; non «160» rivelatori o
 * «200» dispositivi, che sono quantita', non nomi).
 */
function siglaDiProdotto(g: string): boolean {
  if (g.length < 3 || !/\d/.test(g)) return false;
  if (/^\d+(V|A|MA|AH|W|M|MM|CM|KM|DB|OHM|HZ|S|H|MIN|GB|MB|KB|G|KG)$/.test(g)) return false;
  if (/^\d+$/.test(g) && g.length < 4) return false;
  return true;
}

export function fratelloDellaDomanda(schema: SchemaRiferimento, domanda?: string | null): boolean {
  const d = gettoni(domanda ?? "");
  if (d.size === 0) return false;
  const parole = paroleDistintive(schema.modello);
  // Una sigla dello schema che nella domanda NON c'e', mentre c'e' la sua
  // gemella: la domanda parla dell'altra (8200G chiesta, 8200N nel disegno).
  // Se la domanda nomina anche la sigla dello schema («differenze fra 8200G
  // e 8200N»), non e' un fratello: e' un confronto, e decide il resto.
  // La presenza si guarda anche a pezzi adiacenti incollati («am 8200 n» ha
  // «8200N»), se no la grafia staccata farebbe scattare la gemella «8200».
  const dSequenze = gettoniESequenze(domanda ?? "");
  const gemellaChiesta = parole.some(
    (p) => !dSequenze.has(p) && [...d].some((g) => sigleGemelle(p, g)),
  );
  if (gemellaChiesta) return true;
  // La domanda nomina una sigla con cifre (AM-8100, 221-21X, SmartLiving 515)
  // e lo schema ne ha una sua, e non ne hanno nessuna in comune: il disegno
  // e' di un altro prodotto, qualunque cosa dica la risposta. 06/09/2026,
  // banco G08: «schema dei morsetti della AM-8100», la morsettiera della
  // AM-8100 era fra le fonti, ma quella della AM-8200N arrivava prima, aveva
  // gli stessi punti e passava perche' la risposta la nominava in una frase
  // NEGATA («senza ricavarli da schemi della AM-8200N»). A pari punti vince
  // il primo: si mostrava la centrale sbagliata con quella giusta a un passo.
  const sigleDomanda = [...d].filter(siglaDiProdotto);
  const sigleSchema = parole.filter(siglaDiProdotto);
  // «AM8200N» scritto tutto attaccato e' un gettone solo: il nome intero
  // compattato conta come sigla presente.
  const nomeIntero = compatta(schema.modello);
  if (
    sigleDomanda.length > 0 &&
    sigleSchema.length > 0 &&
    !dSequenze.has(nomeIntero) &&
    !sigleSchema.some((p) => dSequenze.has(p))
  ) {
    return true;
  }
  if (parole.length < 2) return false; // un nome di una parola sola non ha fratelli riconoscibili
  const condivise = parole.filter((p) => d.has(p));
  // Nessun minimo di lunghezza sulle mancanti: fino al 05/09 contavano solo
  // da 4 caratteri in su, e «21I» (3) non separava il 221-21I dal 221-21X.
  const mancanti = parole.filter((p) => !d.has(p));
  return condivise.length > 0 && mancanti.length > 0;
}

export function schemaDaMostrare(
  schema: SchemaRiferimento,
  risposta?: string | null,
  domanda?: string | null,
): boolean {
  if (!schemaCoerente(schema, risposta, domanda)) return false;
  if (fratelloDellaDomanda(schema, domanda)) return false;
  return !tipoInConflitto(schema, `${risposta ?? ""} ${domanda ?? ""}`);
}

/**
 * Fra piu' schemi ammissibili, quello giusto.
 *
 * Non basta scartare gli sbagliati: il recupero mette spesso in cima la pagina
 * meno pertinente. Alla domanda sui morsetti del loop dell'AM-8200N la prima
 * citazione era p024, la scheda di ESPANSIONE LIB-8200N, e solo la seconda la
 * p018, la morsettiera CNL che l'installatore aveva chiesto: e' quello che
 * Davide ha visto nell'app il 04/09 (e di nuovo il 05/09, dopo che la pulizia
 * dei doppioni aveva dato alla p024 il tipo «loop»). Quindi si sceglie per
 * merito, non per ordine di arrivo:
 *   3 punti - e' la morsettiera, e la DOMANDA chiede i morsetti;
 *   2 punti - il tipo dello schema e' proprio quello di cui si parla
 *             (schema "loop" e la risposta parla di loop);
 *   1 punto - tipo neutro o non riconosciuto, nessun conflitto;
 *   scartato - modello diverso, o tipo in conflitto (vedi schemaDaMostrare).
 * A pari punteggio vince chi il recupero ha messo prima.
 */
/**
 * Le parole con cui si chiede LA MORSETTIERA: la pagina coi numeri dei
 * morsetti, quella che l'installatore vuole quando dice «morsetti».
 *
 * 05/09/2026 (audit, Fable 7): alla domanda «quali sono i morsetti del loop
 * di una AM-8200N» la pagina della morsettiera CNL (tipo «morsettiera», neutro,
 * 1 punto) perdeva contro la scheda di espansione, che la pulizia dei doppioni
 * aveva lasciato col tipo «loop» (2 punti perche' la risposta parla di loop).
 * Ora la morsettiera vale un tipo pieno: 3 punti se la DOMANDA chiede i
 * morsetti, 2 se ne parla la risposta. Non entra nel conflitto dei tipi
 * (tipoInConflitto): mostra loop, sirene e rele' insieme, quindi non e' mai
 * «un altro tipo» per nessuno.
 */
const PAROLE_DELLA_MORSETTIERA = /\b(morsett|terminal|pin\b)/i;

function puntiSchema(
  schema: SchemaRiferimento,
  risposta: string,
  domanda: string,
): number {
  const tipo = schema.tipo.toLowerCase();
  if (tipo.includes("morsettier")) {
    if (PAROLE_DELLA_MORSETTIERA.test(domanda)) return 3;
    if (PAROLE_DELLA_MORSETTIERA.test(risposta)) return 2;
    return 1;
  }
  const mio = Object.keys(PAROLE_DEL_TIPO).find((k) => tipo.includes(k));
  return mio && PAROLE_DEL_TIPO[mio].test(`${risposta} ${domanda}`) ? 2 : 1;
}

export function scegliSchema(
  schemi: (SchemaRiferimento | null)[],
  risposta?: string | null,
  domanda?: string | null,
): SchemaRiferimento | null {
  let scelto: SchemaRiferimento | null = null;
  let meglio = 0;
  for (const s of schemi) {
    if (!s || !schemaDaMostrare(s, risposta, domanda)) continue;
    const p = puntiSchema(s, risposta ?? "", domanda ?? "");
    if (p > meglio) {
      meglio = p;
      scelto = s;
    }
  }
  return scelto;
}
